import { geometryKey } from './io/missionRoute';
import { fallbackNameFor } from '../missions/namedRouteEditor';
import {
  PlannerAction,
  PlannerSnapshot,
  PlannerState,
  RouteOptions,
  Waypoint,
} from './types';

export const MAX_WAYPOINTS = 25;
const MAX_HISTORY = 50;

let waypointSeq = 0;

export function makeWaypointId(): string {
  waypointSeq += 1;
  return `wp-${Date.now().toString(36)}-${waypointSeq}`;
}

export const DEFAULT_OPTIONS: RouteOptions = {
  profile: 'driving-traffic',
  exclude: { toll: false, motorway: false, ferry: false, unpaved: false },
  departAt: null,
  units: 'mi',
};

export function initialPlannerState(overrides?: Partial<PlannerState>): PlannerState {
  return {
    phase: 'idle',
    waypoints: [],
    options: { ...DEFAULT_OPTIONS, exclude: { ...DEFAULT_OPTIONS.exclude } },
    routes: [],
    primaryIndex: 0,
    alternateIndex: null,
    primaryName: '',
    alternateName: '',
    restoreAlternateKey: null,
    hoverIndex: null,
    failedLegs: [],
    error: null,
    past: [],
    future: [],
    savedRouteId: null,
    dirty: false,
    requestSeq: 0,
    ...overrides,
  };
}

function snapshot(state: PlannerState): PlannerSnapshot {
  return {
    waypoints: state.waypoints,
    options: state.options,
    primaryIndex: state.primaryIndex,
  };
}

/**
 * Apply an operator mutation: pushes an undo snapshot, clears redo, and drops
 * routes that no longer describe the waypoint list. Routing results never call
 * this, so Ctrl+Z always steps back through operator actions.
 */
function mutate(state: PlannerState, next: Partial<PlannerSnapshot>): PlannerState {
  const waypoints = next.waypoints ?? state.waypoints;
  return {
    ...state,
    ...next,
    phase: waypoints.length < 2 ? 'idle' : 'placing',
    routes: next.waypoints ? [] : state.routes,
    // An alternate is a choice between the options currently on the table. Any
    // change that re-asks the routing engine invalidates that choice, and
    // silently carrying the index over would point it at a different road.
    alternateIndex: null,
    restoreAlternateKey: null,
    failedLegs: [],
    error: null,
    past: [...state.past, snapshot(state)].slice(-MAX_HISTORY),
    future: [],
    dirty: true,
  };
}

export function plannerReducer(state: PlannerState, action: PlannerAction): PlannerState {
  switch (action.type) {
    case 'ADD_WAYPOINT': {
      if (state.waypoints.length >= MAX_WAYPOINTS) {
        return { ...state, error: { message: `A route supports at most ${MAX_WAYPOINTS} waypoints.`, retryable: false } };
      }
      const wp: Waypoint = {
        id: makeWaypointId(),
        longitude: action.longitude,
        latitude: action.latitude,
        label: action.label,
        address: action.address,
        labelStatus: action.label ? 'done' : 'idle',
        source: action.source ?? 'click',
      };
      return mutate(state, { waypoints: [...state.waypoints, wp], primaryIndex: 0 });
    }

    case 'INSERT_WAYPOINT': {
      if (state.waypoints.length >= MAX_WAYPOINTS) {
        return { ...state, error: { message: `A route supports at most ${MAX_WAYPOINTS} waypoints.`, retryable: false } };
      }
      const index = Math.max(0, Math.min(state.waypoints.length, action.index));
      const wp: Waypoint = {
        id: makeWaypointId(),
        longitude: action.longitude,
        latitude: action.latitude,
        labelStatus: 'idle',
        source: action.source ?? 'drag-insert',
      };
      const waypoints = [...state.waypoints];
      waypoints.splice(index, 0, wp);
      return mutate(state, { waypoints, primaryIndex: 0 });
    }

    case 'MOVE_WAYPOINT': {
      const waypoints = state.waypoints.map((w) =>
        w.id === action.id
          ? { ...w, longitude: action.longitude, latitude: action.latitude, label: undefined, address: undefined, labelStatus: 'idle' as const }
          : w,
      );
      return mutate(state, { waypoints, primaryIndex: 0 });
    }

    case 'DELETE_WAYPOINT': {
      const waypoints = state.waypoints.filter((w) => w.id !== action.id);
      if (waypoints.length === state.waypoints.length) return state;
      return mutate(state, { waypoints, primaryIndex: 0 });
    }

    case 'REORDER_WAYPOINTS': {
      const { fromIndex, toIndex } = action;
      if (
        fromIndex === toIndex
        || fromIndex < 0 || fromIndex >= state.waypoints.length
        || toIndex < 0 || toIndex >= state.waypoints.length
      ) {
        return state;
      }
      const waypoints = [...state.waypoints];
      const [moved] = waypoints.splice(fromIndex, 1);
      waypoints.splice(toIndex, 0, moved);
      return mutate(state, { waypoints, primaryIndex: 0 });
    }

    case 'SET_WAYPOINT_NOTES': {
      const notes = action.notes.trim();
      const target = state.waypoints.find((w) => w.id === action.id);
      if (!target || (target.notes || '') === notes) return state;
      // Notes carry no geometry, so the current routes stay valid.
      return {
        ...state,
        waypoints: state.waypoints.map((w) => (
          w.id === action.id ? { ...w, notes: notes || undefined } : w
        )),
        past: [...state.past, snapshot(state)].slice(-MAX_HISTORY),
        future: [],
        dirty: true,
      };
    }

    case 'REVERSE_ROUTE': {
      if (state.waypoints.length < 2) return state;
      return mutate(state, { waypoints: [...state.waypoints].reverse(), primaryIndex: 0 });
    }

    case 'CLEAR': {
      if (state.waypoints.length === 0) return state;
      return {
        ...mutate(state, { waypoints: [], primaryIndex: 0 }),
        routes: [],
        primaryName: '',
        alternateName: '',
        savedRouteId: null,
        dirty: false,
      };
    }

    case 'SET_WAYPOINTS': {
      const waypoints = action.waypoints.slice(0, MAX_WAYPOINTS);
      if (action.resetHistory) {
        return {
          ...state,
          waypoints,
          primaryIndex: 0,
          routes: [],
          failedLegs: [],
          error: null,
          past: [],
          future: [],
          dirty: false,
          phase: waypoints.length < 2 ? 'idle' : 'placing',
        };
      }
      return mutate(state, { waypoints, primaryIndex: 0 });
    }

    case 'RESET': {
      // Loading a different mission or saved route: a clean slate, no undo bridge.
      const options: RouteOptions = {
        ...DEFAULT_OPTIONS,
        ...action.options,
        exclude: { ...DEFAULT_OPTIONS.exclude, ...(action.options?.exclude || {}) },
      };
      return initialPlannerState({
        waypoints: action.waypoints || [],
        options,
        primaryName: action.primaryName || '',
        alternateName: action.alternateName || '',
        restoreAlternateKey: action.restoreAlternateKey ?? null,
        phase: (action.waypoints || []).length < 2 ? 'idle' : 'placing',
        requestSeq: state.requestSeq,
      });
    }

    case 'LABEL_LOADING': {
      const ids = new Set(action.ids);
      return {
        ...state,
        waypoints: state.waypoints.map((w) => (ids.has(w.id) ? { ...w, labelStatus: 'loading' } : w)),
      };
    }

    case 'LABEL_RESOLVED': {
      const byId = new Map(action.labels.map((l) => [l.id, l]));
      return {
        ...state,
        waypoints: state.waypoints.map((w) => {
          const hit = byId.get(w.id);
          if (!hit) return w;
          return {
            ...w,
            label: hit.label ?? w.label,
            address: hit.address ?? w.address,
            labelStatus: hit.label ? 'done' : 'failed',
          };
        }),
      };
    }

    case 'SET_OPTIONS': {
      const options: RouteOptions = {
        ...state.options,
        ...action.options,
        exclude: { ...state.options.exclude, ...(action.options.exclude || {}) },
      };
      // depart_at only means something on the traffic profile.
      if (options.profile !== 'driving-traffic') options.departAt = null;
      return { ...mutate(state, { options, primaryIndex: 0 }), routes: [] };
    }

    case 'SET_UNITS':
      // A display preference, not a route mutation — stays out of undo history.
      return { ...state, options: { ...state.options, units: action.units } };

    case 'BEGIN_EDIT':
      return state.phase === 'editing' ? state : { ...state, phase: 'editing' };

    case 'ROUTE_START':
      return { ...state, phase: 'routing', error: null, requestSeq: action.seq };

    case 'ROUTE_SUCCESS': {
      if (action.seq !== state.requestSeq) return state;
      const primaryIndex = Math.min(state.primaryIndex, Math.max(0, action.routes.length - 1));
      let alternateIndex = state.alternateIndex;
      if (alternateIndex != null && (alternateIndex === primaryIndex || alternateIndex >= action.routes.length)) {
        alternateIndex = null;
      }
      if (alternateIndex == null && state.restoreAlternateKey) {
        const match = action.routes.findIndex((route, index) => (
          index !== primaryIndex && geometryKey(route.geometry) === state.restoreAlternateKey
        ));
        if (match >= 0) alternateIndex = match;
      }
      return {
        ...state,
        phase: 'ready',
        routes: action.routes,
        primaryIndex,
        alternateIndex,
        failedLegs: action.failedLegs ?? [],
        error: null,
      };
    }

    case 'ROUTE_EMPTY': {
      if (action.seq !== state.requestSeq) return state;
      return {
        ...state,
        phase: 'error',
        routes: [],
        error: { message: action.message, retryable: false },
      };
    }

    case 'ROUTE_ERROR': {
      if (action.seq !== state.requestSeq) return state;
      return {
        ...state,
        phase: 'error',
        error: { message: action.message, retryable: action.retryable ?? true },
      };
    }

    case 'SET_PRIMARY': {
      if (action.index < 0 || action.index >= state.routes.length || action.index === state.primaryIndex) return state;
      return {
        ...state,
        primaryIndex: action.index,
        // Promoting the alternate leaves the mission with one route named
        // twice, so the slot is vacated rather than left pointing at the road
        // we are now taking.
        alternateIndex: state.alternateIndex === action.index ? null : state.alternateIndex,
        dirty: true,
      };
    }

    case 'SET_ALTERNATE': {
      const { index } = action;
      if (index === null) {
        return { ...state, alternateIndex: null, restoreAlternateKey: null, dirty: true };
      }
      if (index < 0 || index >= state.routes.length || index === state.primaryIndex) return state;
      // Clicking the current alternate again clears it, so the same control
      // both sets and unsets and there is no second button to find.
      if (state.alternateIndex === index) {
        return { ...state, alternateIndex: null, restoreAlternateKey: null, dirty: true };
      }
      return {
        ...state,
        alternateIndex: index,
        restoreAlternateKey: null,
        alternateName: state.alternateName.trim()
          ? state.alternateName
          : fallbackNameFor(state.primaryName),
        dirty: true,
      };
    }

    case 'SET_ROUTE_NAME': {
      if (action.role === 'primary') {
        return state.primaryName === action.name
          ? state
          : { ...state, primaryName: action.name, dirty: true };
      }
      return state.alternateName === action.name
        ? state
        : { ...state, alternateName: action.name, dirty: true };
    }

    case 'HOVER_ROUTE':
      return state.hoverIndex === action.index ? state : { ...state, hoverIndex: action.index };

    case 'UNDO': {
      const prev = state.past[state.past.length - 1];
      if (!prev) return state;
      return {
        ...state,
        ...prev,
        phase: prev.waypoints.length < 2 ? 'idle' : 'placing',
        routes: [],
        failedLegs: [],
        error: null,
        past: state.past.slice(0, -1),
        future: [snapshot(state), ...state.future].slice(0, MAX_HISTORY),
        dirty: true,
      };
    }

    case 'REDO': {
      const next = state.future[0];
      if (!next) return state;
      return {
        ...state,
        ...next,
        phase: next.waypoints.length < 2 ? 'idle' : 'placing',
        routes: [],
        failedLegs: [],
        error: null,
        past: [...state.past, snapshot(state)].slice(-MAX_HISTORY),
        future: state.future.slice(1),
        dirty: true,
      };
    }

    case 'MARK_SAVED':
      return { ...state, savedRouteId: action.savedRouteId, dirty: false };

    default:
      return state;
  }
}
